// The DOM id a field-keyed input wears, and the one place the namespacing
// convention lives.
//
// This form's standing contract is that an input's DOM id EQUALS its field key,
// which is what lets the editor's failure summary scroll to a miss by key alone.
// The contract holds only while a key renders once. It stops holding the moment a
// control is mounted per LINE — a quote holds several projects rendered from the
// same schema, and an `assembly` item brings a whole second Assembly panel with
// it — so a bare id would be duplicated in the document: `getElementById` finds
// only the first, `<label htmlFor>` binds to the wrong control, and the inline
// error lights up on every copy.
//
// The fix is the prefix SchemaForm already spends on its per-line fields
// (`<local_id>::<field_key>`, see `fieldIdPrefix` there and `lineFieldKey` in
// QuoteEdit). SchemaForm takes it as a prop because it threads it to one place;
// the Assembly panel spreads across five components and dozens of hand-written
// ids, so it reads the same value from context instead — one provider at the
// mount, no chance of a site being missed.
//
// ERROR MAP KEYS ARE NOT PREFIXED HERE. A control is handed errors already keyed
// by bare field key (QuoteEdit strips its own line prefix back off before passing
// them down), exactly as SchemaForm expects them. Only the id is namespaced.

import { createContext, useContext } from 'react';

// Empty string ⇒ the singleton mount: ids stay bare, which is what every
// quote-level surface renders today and what the field-key anchors point at.
export const FieldIdPrefixContext = createContext<string>('');

export function fieldDomId(prefix: string, fieldKey: string): string {
  return prefix ? `${prefix}::${fieldKey}` : fieldKey;
}

// The DOM id for `fieldKey` under whatever mount is rendering. Returns the bare
// key at the singleton mount, so an unprefixed caller is unchanged.
export function useFieldDomId(fieldKey: string): string {
  return fieldDomId(useContext(FieldIdPrefixContext), fieldKey);
}

// The prefix itself, for a caller that builds several ids off one key (a row's
// own sub-ids, a photo slot) and would otherwise call the hook in a loop.
export function useFieldIdPrefix(): string {
  return useContext(FieldIdPrefixContext);
}
